import type { DeletedExperimentSessionItem } from '@/types';

import React from 'react';
import { Link } from 'react-router-dom';
import { RiRefreshLine, RiFileCopyLine, RiErrorWarningLine } from '@remixicon/react';
import {
  Button,
  Chip,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Tooltip,
  useDisclosure
} from '@heroui/react';
import { useAtom, useAtomValue } from 'jotai';

import { TablePage, ColumnsToggleDropdown, TableFilters } from '@/components/table';
import {
  deletedSessionsListParamsAtom,
  deletedSessionsQueryAtom,
  restoreDeletedSession
} from '@/store';
import { PermissionGuard } from '@/components/shared/PermissionGuard';
import { useDeletedSessionsSorting } from '@/hooks/deleted-sessions';
import { useVisibleColumns } from '@/hooks/table';
import { SearchInput } from '@/components/shared/SearchInput';
import { DateRangeFilter } from '@/components/shared/DateRangeFilter';
import { FingerprintChip } from '@/components/shared/FingerprintChip';
import { DateTimeDisplay } from '@/components/shared/DateTimeDisplay';
import { copyToClipboard } from '@/utils/helpers';

const lifecycleColor = (state: string): 'success' | 'warning' | 'default' | 'primary' => {
  switch (state) {
    case 'live':
      return 'success';
    case 'hold':
      return 'warning';
    case 'final':
      return 'primary';
    default:
      return 'default';
  }
};

export default function SessionsDeletedPage() {
  const { data, isLoading, isError, error } = useAtomValue(deletedSessionsQueryAtom);
  const [params, setParams] = useAtom(deletedSessionsListParamsAtom);

  const sessions = React.useMemo<DeletedExperimentSessionItem[]>(() => data?.data ?? [], [data]);

  const [searchTerm, setSearchTerm] = React.useState('');
  const [dateFilter, setDateFilter] = React.useState<{ start: Date | null; end: Date | null }>({
    start: null,
    end: null
  });

  const { sortDescriptor, setSortDescriptor, sortedSessions } = useDeletedSessionsSorting(sessions);

  const handlePageChange = React.useCallback(
    (page: number) => {
      setParams((prev) => ({ ...prev, page }));
    },
    [setParams]
  );

  const handleSearch = React.useCallback(() => {
    setParams((prev) => ({
      ...prev,
      page: 1,
      search: searchTerm || undefined
    }));
  }, [searchTerm, setParams]);

  const handleClearSearch = React.useCallback(() => {
    setSearchTerm('');
    setParams((prev) => ({
      ...prev,
      page: 1,
      search: undefined,
      refresh: Date.now()
    }));
  }, [setParams]);

  const clearFilters = React.useCallback(() => {
    setSearchTerm('');
    setDateFilter({ start: null, end: null });
    setParams((prev) => ({
      ...prev,
      page: 1,
      filters: {},
      refresh: Date.now()
    }));
  }, [setParams]);

  const handleDateChange = React.useCallback(
    (value: { start: Date | null; end: Date | null }) => {
      setDateFilter(value);

      let dateFilterValue = '';
      if (value.start && value.end) {
        const startDate = value.start.toISOString().split('T')[0];
        const endDate = value.end.toISOString().split('T')[0];
        dateFilterValue = `${startDate},${endDate}`;
      } else if (value.start) {
        dateFilterValue = value.start.toISOString().split('T')[0];
      }

      setParams((prev) => ({
        ...prev,
        page: 1,
        filters: {
          ...prev.filters,
          date: dateFilterValue || undefined
        }
      }));
    },
    [setParams]
  );

  const hasAnyFilter = React.useMemo(() => {
    return Boolean(searchTerm) || Boolean(dateFilter.start || dateFilter.end);
  }, [searchTerm, dateFilter]);

  const triggerRefresh = React.useCallback(() => {
    setParams((prev) => ({ ...prev, refresh: Date.now() }));
  }, [setParams]);

  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const [restoringPublicId, setRestoringPublicId] = React.useState<string | null>(null);
  const [isRestoring, setIsRestoring] = React.useState(false);

  const requestRestore = React.useCallback(
    (publicId: string) => {
      setRestoringPublicId(publicId);
      onOpen();
    },
    [onOpen]
  );

  const confirmRestore = React.useCallback(async () => {
    if (!restoringPublicId) return;

    try {
      setIsRestoring(true);
      const response = await restoreDeletedSession(restoringPublicId);

      if ((response as { meta?: { message?: string } })?.meta?.message) {
        const { toast } = await import('react-toastify');
        toast.success((response as { meta: { message: string } }).meta.message);
      }

      triggerRefresh();
      setIsRestoring(false);
      onOpenChange();
      setRestoringPublicId(null);
    } catch {
      setIsRestoring(false);
    }
  }, [restoringPublicId, onOpenChange, triggerRefresh]);

  const renderCell = React.useCallback(
    (item: DeletedExperimentSessionItem, columnKey: React.Key) => {
      switch (columnKey) {
        case 'session_short_id':
          return (
            <Button
              size="sm"
              variant="light"
              className="font-mono text-xs"
              startContent={<RiFileCopyLine className="h-3 w-3" />}
              onPress={() =>
                copyToClipboard(item.session_short_id, 'Session short ID copied to clipboard')
              }
            >
              {item.session_short_id}
            </Button>
          );
        case 'experiment': {
          if (!item.experiment) return <span className="text-default-400">—</span>;
          return (
            <Link
              to={`/experiments/${item.experiment.id}`}
              className="text-primary hover:underline text-sm"
            >
              {item.experiment.name || item.experiment.title}
            </Link>
          );
        }
        case 'domain':
          return (
            <span className="text-sm">{item.domain?.domain ?? '—'}</span>
          );
        case 'fingerprint':
          return item.fingerprint ? (
            <FingerprintChip fingerprint={item.fingerprint} maxLength={14} />
          ) : (
            <span className="text-default-400">—</span>
          );
        case 'lifecycle_state':
          return (
            <Chip size="sm" variant="flat" color={lifecycleColor(item.lifecycle_state)}>
              {item.lifecycle_state}
            </Chip>
          );
        case 'deleted_at':
          return <DateTimeDisplay dateString={item.deleted_at} />;
        case 'actions':
          return (
            <div className="flex justify-end">
              <PermissionGuard permission="ExperimentSession.restore">
                <Tooltip content="Restore session">
                  <Button
                    isIconOnly
                    color="warning"
                    size="sm"
                    variant="light"
                    onPress={() => requestRestore(item.public_id)}
                  >
                    <RiRefreshLine className="h-4 w-4" />
                  </Button>
                </Tooltip>
              </PermissionGuard>
            </div>
          );
        default:
          return null;
      }
    },
    [requestRestore]
  );

  const renderCollapseContent = React.useCallback((item: DeletedExperimentSessionItem) => {
    return (
      <div className="space-y-4 p-4">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
          <div className="border-default-200 bg-default-50 dark:border-default-800 dark:bg-default-900/50 rounded-lg border p-3">
            <p className="text-default-500 dark:text-default-400 mb-1 text-xs font-medium">Public ID</p>
            <p className="font-mono text-xs break-all">{item.public_id}</p>
          </div>
          <div className="border-default-200 bg-default-50 dark:border-default-800 dark:bg-default-900/50 rounded-lg border p-3">
            <p className="text-default-500 dark:text-default-400 mb-1 text-xs font-medium">Matched At</p>
            {item.matched_at ? (
              <DateTimeDisplay dateString={item.matched_at} showSeconds />
            ) : (
              <span className="text-default-400">—</span>
            )}
          </div>
          <div className="border-default-200 bg-default-50 dark:border-default-800 dark:bg-default-900/50 rounded-lg border p-3">
            <p className="text-default-500 dark:text-default-400 mb-1 text-xs font-medium">Created At</p>
            <DateTimeDisplay dateString={item.created_at} showSeconds />
          </div>
        </div>
        {item.url && (
          <div className="border-default-200 bg-default-50 dark:border-default-800 dark:bg-default-900/50 rounded-lg border p-3">
            <p className="text-default-500 dark:text-default-400 mb-1 text-xs font-medium">URL</p>
            <p className="text-sm break-all">{item.url}</p>
          </div>
        )}
        {item.organization && (
          <p className="text-default-500 text-xs">
            Workspace: <span className="text-foreground">{item.organization.name}</span>
          </p>
        )}
      </div>
    );
  }, []);

  const columns = React.useMemo(
    () => [
      { name: 'Short ID', uid: 'session_short_id', sortable: true },
      { name: 'Experiment', uid: 'experiment', sortable: true },
      { name: 'Domain', uid: 'domain', sortable: true },
      { name: 'Fingerprint', uid: 'fingerprint', sortable: true },
      { name: 'Lifecycle', uid: 'lifecycle_state', sortable: true },
      { name: 'Deleted At', uid: 'deleted_at', sortable: true },
      { name: 'Actions', uid: 'actions', align: 'end', cellClass: 'text-right' }
    ],
    []
  );

  const { visibleColumns, setVisibleColumns, headerColumns } = useVisibleColumns(columns);

  return (
    <TablePage
      columns={columns}
      currentPage={data?.meta?.current_page}
      data={sortedSessions}
      emptyMessage="No deleted sessions found"
      error={error}
      errorMessage="Failed to load deleted sessions"
      enableCollapse={true}
      renderCollapseContent={renderCollapseContent}
      filtersSection={
        <TableFilters
          hasActiveFilters={hasAnyFilter}
          isRefreshing={isLoading}
          onClearFilters={clearFilters}
          onRefresh={triggerRefresh}
        >
          <SearchInput
            placeholder="Search sessions..."
            value={searchTerm}
            onValueChange={setSearchTerm}
            onSearch={handleSearch}
            onClear={handleClearSearch}
          />

          <ColumnsToggleDropdown
            allColumns={columns.map((c) => ({ uid: c.uid, name: c.name }))}
            selected={visibleColumns}
            onChange={(next) => setVisibleColumns(next)}
          />

          <DateRangeFilter label="Deleted Date" value={dateFilter} onChange={handleDateChange} />
        </TableFilters>
      }
      headerColumns={headerColumns}
      isError={isError}
      isLoading={isLoading}
      renderCell={renderCell}
      sortDescriptor={(sortDescriptor || { column: 'deleted_at', direction: 'descending' }) as any}
      title="Deleted Sessions"
      totalPages={data?.meta?.last_page}
      onPageChange={handlePageChange}
      onRefresh={triggerRefresh}
      onSortChange={(d) =>
        setSortDescriptor({
          column: d.column as keyof DeletedExperimentSessionItem,
          direction: d.direction as 'ascending' | 'descending'
        })
      }
    >
      <Modal isOpen={isOpen} placement="center" onOpenChange={onOpenChange}>
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex items-center gap-2">
                <RiErrorWarningLine className="text-warning h-5 w-5" />
                <span>Restore session?</span>
              </ModalHeader>
              <ModalBody>
                <p>
                  This will restore the session record in the database. Recording data (QuestDB / MinIO)
                  was permanently removed when the session was deleted and cannot be recovered.
                </p>
              </ModalBody>
              <ModalFooter>
                <Button variant="light" onPress={onClose}>
                  Cancel
                </Button>
                <Button color="success" isLoading={isRestoring} onPress={confirmRestore}>
                  Restore
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </TablePage>
  );
}
